const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://niftee-orchestrator.jason-a6e.workers.dev';

export interface DashboardUser {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'user';
}

export interface SessionResponse {
  user: DashboardUser;
  session: {
    expiresAt: string;
  };
}

export interface VerifyResponse {
  success: boolean;
  session: {
    token: string;
    expiresAt: string;
  };
  user: DashboardUser;
}

export interface UserListItem {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'user';
  status: 'active' | 'invited' | 'disabled';
  created_at: string;
  last_login_at: string | null;
  invited_by_name: string | null;
  invited_by_email: string | null;
}

export class AuthError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Send magic link to email
 */
export async function sendMagicLink(email: string): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_URL}/api/auth/send-magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Failed to send magic link', response.status);
  }

  return response.json();
}

/**
 * Verify magic link token and get session
 */
export async function verifyMagicLink(token: string): Promise<VerifyResponse> {
  const response = await fetch(`${API_URL}/api/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Invalid or expired link', response.status);
  }

  return response.json();
}

/**
 * Get current session (validate token)
 */
export async function getSession(token: string): Promise<SessionResponse> {
  const response = await fetch(`${API_URL}/api/auth/session`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Invalid session', response.status);
  }

  return response.json();
}

/**
 * Logout (invalidate session)
 */
export async function logout(token: string): Promise<void> {
  await fetch(`${API_URL}/api/auth/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ============================================
// ADMIN USER MANAGEMENT
// ============================================

/**
 * List all dashboard users (admin only)
 */
export async function listUsers(token: string): Promise<{ users: UserListItem[] }> {
  const response = await fetch(`${API_URL}/api/admin/users`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Failed to list users', response.status);
  }

  return response.json();
}

/**
 * Invite a new user (admin only)
 */
export async function inviteUser(
  token: string,
  data: { email: string; name?: string; role: 'admin' | 'user' }
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_URL}/api/admin/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Failed to invite user', response.status);
  }

  return response.json();
}

/**
 * Update a user (admin only)
 */
export async function updateUser(
  token: string,
  userId: string,
  data: { role?: 'admin' | 'user'; status?: 'active' | 'disabled'; name?: string }
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Failed to update user', response.status);
  }

  return response.json();
}

/**
 * Delete (disable) a user (admin only)
 */
export async function deleteUser(token: string, userId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AuthError(error.error || 'Failed to delete user', response.status);
  }

  return response.json();
}
